import type { MenuNode } from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { categoryIdForStatusCategory } from '@atlassian/jira-common-constants/src/status-categories.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useBoardSelector, useBoardActionCreator } from '../../../../../../../../state/index.tsx';
import {
	cardTransitionSourceStatusSelector,
	cardTransitionsColumnsSelector,
} from '../../../../../../../../state/selectors/card-transitions/card-transitions-selectors.tsx';
import { issueRankTransitionRequest } from '../../../../../../../../state/actions/issue/rank-transition/index.tsx';
import { boardIssuesSelector } from '../../../../../../../../state/selectors/issue/board-issue-selectors.tsx';
import type { Props } from './types.tsx';
import { useChangeStatusContent } from './view.tsx';

const FIELD_KEY = FIELD_TYPE_MAP[STATUS_TYPE];

export const useChangeStatus = ({ issueId }: Props): MenuNode[] => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({});

	const allIssues = useBoardSelector(boardIssuesSelector);
	const issue = allIssues[String(issueId)];

	const { transitionsByStatusName } = useBoardSelector((state) =>
		issue ? cardTransitionsColumnsSelector(state, issue) : { transitionsByStatusName: new Map() },
	);

	const getSourceStatus = fg('one_event_iteration_3')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBoardSelector(cardTransitionSourceStatusSelector)
		: () => null;

	const onChangeStatus = useBoardActionCreator((targetStatusName: string) => {
		const transition = transitionsByStatusName.get(targetStatusName);

		if (!transition) {
			throw new Error(`No transition found for status: ${targetStatusName}`);
		}

		const { destinationColumnId, transitionId } = transition;
		const sourceStatus = fg('one_event_iteration_3') ? getSourceStatus(issue.id) : null;

		fg('one_event_iteration_3') &&
			getUpdateAnalyticsFlowHelper().fireAnalyticsEnd(FIELD_KEY, {
				analytics: createAnalyticsEvent({}),
				attributes: {
					fieldType: STATUS_TYPE,
					oldValId: issue?.statusId,
					newValId: transition.targetStatusId,
					oldStatusCategoryId: categoryIdForStatusCategory(sourceStatus?.category),
					newStatusCategoryId: categoryIdForStatusCategory(transition.targetStatusCategory),
					...(fg('one_event_status_update_fix') && {
						isContextMenuEditing: true,
						isIssueViewEditing: false,
					}),
				},
			});

		return issueRankTransitionRequest({
			issueIds: [issue.id],
			sourceColumnId: issue.columnId,
			destinationColumnId,
			transitionId,
			rankBeforeIssueId: null,
			rankAfterIssueId: null,
			sourceSwimlaneId: null,
			destinationSwimlaneId: null,
			isSourceDone: false,
			isDestinationDone: false,
			analyticsEvent,
		});
	});

	return useChangeStatusContent({
		onChangeStatus,
		transitionsByStatusName,
	});
};
